// LeadFilterSections.tsx - Extract UI sections from LeadFilter

import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { DatePicker } from 'antd';
import moment from 'moment';
import { sortLeadBy, dateLeadAdded, operatorOptions } from './LeadFilterUtils';
import { getAllProducts, Product } from '../../services/productService';
import logger from '../../utils/logger';

// Props needed for each section - include everything they might need
export interface SectionProps {
  filterData: any;
  handleFilter: (e: any, name: string) => void;
  dateFilterOnChange: (e: any, name: string) => void;
  advanceDateFilterOnChange?: (e: any, name: string, callback: (date: any) => void) => void;
  
  // State variables
  startDate?: any;
  endDate?: any;
  showDatePicker?: boolean;
  datePickerKey?: string;
  
  // Callbacks
  setTaskFromDate?: (date: any) => void;
  setTaskToDate?: (date: any) => void;
  setActivityFromDate?: (date: any) => void;
  setActivityToDate?: (date: any) => void;
  
  // Data arrays
  labels?: any[];
  status?: any[];
  leadSource?: any[];
  teamMembers?: any[];
  employee?: any[];
  customLeadOptions?: any[];
  activityTypes?: any[];
  taskOptions?: any[];
    products?: Product[]; 
  productsLoading?: boolean; 
  
  // Helper methods
  getOperatorOptions?: () => any[];
  isOptionsExist?: () => any[];
  isOrganization?: boolean;
}

// Basic Filters Section
export const BasicFiltersSection: React.FC<SectionProps> = ({
  filterData,
  handleFilter,
  dateFilterOnChange,
  startDate,
  endDate,
  showDatePicker,
  labels,
  status,
  leadSource,
}) => {
  return (
    <>
      <div className="form-group">
        <label htmlFor="lead-sort" className="form-label">
          Sort lead by
        </label>
        <Select
          value={filterData.sortLeadBy}
          className="basic-single"
          classNamePrefix="select"
          isDisabled={false}
          isLoading={false}
          isClearable={true}
          isRtl={false}
          isSearchable={true}
          name="sortLeadBy"
          options={sortLeadBy}
          onChange={(e: any) => handleFilter(e, "sortLeadBy")}
        />
      </div>

      <div className="form-group">
        <label htmlFor="lead-date" className="form-label">
          Date lead added
        </label>
        <Select
          value={filterData.dateLead}
          className="basic-single"
          classNamePrefix="select"
          isDisabled={false}
          isLoading={false}
          isClearable={true}
          isRtl={false}
          isSearchable={true}
          name="dateLead"
          options={dateLeadAdded}
          onChange={(e: any) => {
            console.log('[BasicFiltersSection] dateLead changed:', e);
            dateFilterOnChange(e, "dateLead");
          }}
        />
      </div>
      
      {showDatePicker && (
  <div className="form-group">
    <label className="form-label">Custom Dates</label>
    <div className="custom_date_picker" id="custom-date-container">
      <DatePicker
        format={"DD MMM YYYY"}
        showToday={false}
        getPopupContainer={() => document.getElementById('custom-date-container') || document.body}
        onChange={(date: any) => {
          if (date && startDate) {
            // Convert to UTC at start of day
            const utcDate = moment.utc(date.format('YYYY-MM-DD')).startOf('day').format();
            startDate(utcDate);
          }
        }}
      />
      <DatePicker
        format={"DD MMM YYYY"}
        showToday={false}
        getPopupContainer={() => document.getElementById('custom-date-container') || document.body}
        onChange={(date: any) => {
          if (date && endDate) {
            // Convert to UTC at end of day
            const utcDate = moment.utc(date.format('YYYY-MM-DD')).endOf('day').format();
            endDate(utcDate);
          }
        }}
      />
    </div>
  </div>
)}

      <div className="form-group">
        <label htmlFor="lead-label" className="form-label">
          Lead Labels
        </label>
        <Select
          value={filterData.label}
          className="basic-single"
          classNamePrefix="select"
          isDisabled={false}
          isLoading={false}
          isClearable={true}
          isRtl={false}
          isMulti
          isSearchable={true}
          name="color"
          options={labels?.map((v: any) => {
            return { value: v?.value, label: v?.name };
          })}
          onChange={(e: any) => handleFilter(e, "label")}
        />
      </div>

      <div className="form-group">
        <label htmlFor="lead-status" className="form-label">
          Lead Status
        </label>
        <Select
          value={filterData.status}
          className="basic-single"
          classNamePrefix="select"
          isDisabled={false}
          isLoading={false}
          isClearable={true}
          isRtl={false}
          isMulti
          isSearchable={true}
          name="color"
          options={status?.map((v: any) => {
            return { value: v?.value, label: v?.name };
          })}
          onChange={(e: any) => handleFilter(e, "status")}
        />
      </div>

      <div className="form-group">
        <label htmlFor="lead-source" className="form-label">
          Lead Sources
        </label>
        <Select
          value={filterData.sources}
          className="basic-single"
          classNamePrefix="select"
          isDisabled={false}
          isLoading={false}
          isClearable={true}
          isRtl={false}
          isMulti
          isSearchable={true}
          name="color"
          options={leadSource?.map((v: any) => {
            return { value: v?.value, label: v?.name };
          })}
          onChange={(e: any) => handleFilter(e, "sources")}
        />
      </div>
    </>
  );
};

// Team Filters Section
export const TeamFiltersSection: React.FC<SectionProps> = ({
  filterData,
  handleFilter,
  teamMembers,
  employee,
}) => {
  return (
    <>
      <div className="form-group">
        <label htmlFor="lead-team" className="form-label">
          Team
        </label>
        <Select
          value={filterData.team}
          className="basic-single"
          classNamePrefix="select"
          isDisabled={false}
          isLoading={false}
          isClearable={true}
          isRtl={false}
          isMulti
          isSearchable={true}
          name="color"
          options={teamMembers?.map((v: any) => {
            return { value: v?._id, label: v?.showName };
          })}
          onChange={(e: any) => handleFilter(e, "team")}
        />
      </div>

      <div className="form-group">
        <label htmlFor="lead-employee" className="form-label">
          Team Member
        </label>
        <Select
          value={filterData.employee}
          className="basic-single"
          classNamePrefix="select"
          isDisabled={false}
          isLoading={false}
          isClearable={true}
          isRtl={false}
          isMulti
          isSearchable={true}
          name="color"
          options={employee?.map((v: any) => {
            return {
              value: v?._id,
              label:
                v?.firstName && v?.lastName
                  ? v?.firstName + " " + v?.lastName
                  : v?.firstName,
            };
          })}
          onChange={(e: any) => handleFilter(e, "employee")}
        />
      </div>

      <div className="form-group">
        <label htmlFor="lead-assigned" className="form-label">
          Lead assigned to
        </label>
        <Select
          isMulti
          value={filterData.assignedLead}
          className="basic-single"
          classNamePrefix="select"
          isDisabled={false}
          isLoading={false}
          isClearable={true}
          isRtl={false}
          isSearchable={true}
          name="color"
          options={employee?.map((v: any) => {
            return {
              value: v?._id,
              label:
                v?.firstName && v?.lastName
                  ? v?.firstName + " " + v?.lastName
                  : v?.firstName,
            };
          })}
          onChange={(e: any) => handleFilter(e, "assignedLead")}
        />
      </div>
    </>
  );
};

// Activity Filters Section
export const ActivityFiltersSection: React.FC<SectionProps> = ({
    filterData,
    handleFilter,
    advanceDateFilterOnChange,
    activityTypes,
    datePickerKey,
    setActivityFromDate,
    setActivityToDate,
  }) => {
    return (
      <div className="activity-filters-section">
        {/* Description */}
        <div className="section-description">
          Filter leads based on their call activity and communication history
        </div>
        
        {/* Call Duration Filter */}
        <div className="filter-group">
          <label className="filter-label">Call Duration</label>
          <div className="filter-row">
            <div className="operator-select">
              <Select
                placeholder="Select operator..."
                value={filterData.durationOperator}
                className="basic-single"
                classNamePrefix="select"
                isDisabled={false}
                isLoading={false}
                isClearable={true}
                isRtl={false}
                isSearchable={true}
                name="durationOperator"
                options={operatorOptions}
                onChange={(e: any) => handleFilter(e, "durationOperator")}
              />
            </div>
            <div className="value-input">
              <input
                type="number"
                min="0"
                placeholder="Duration in seconds"
                className="form-control lead_input"
                value={filterData.callDurationValue}
                onChange={(e: any) => 
                  handleFilter(e.target.value, "callDurationValue")
                }
              />
            </div>
          </div>
          <div className="helper-text">
            Common: 60s (1min), 300s (5min), 600s (10min)
          </div>
        </div>
  
        {/* Number of Calls Filter */}
        <div className="filter-group">
          <label className="filter-label">Number of Calls</label>
          <div className="filter-row">
            <div className="operator-select">
              <Select
                placeholder="Select operator..."
                value={filterData.callCountOperator}
                className="basic-single"
                classNamePrefix="select"
                isDisabled={false}
                isLoading={false}
                isClearable={true}
                isRtl={false}
                isSearchable={true}
                name="callCountOperator"
                options={operatorOptions}
                onChange={(e: any) => handleFilter(e, "callCountOperator")}
              />
            </div>
            <div className="value-input">
              <input
                type="number"
                min="0"
                placeholder="Number of calls"
                className="form-control lead_input"
                value={filterData.callCountValue}
                onChange={(e: any) => 
                  handleFilter(e.target.value, "callCountValue")
                }
              />
            </div>
          </div>
        </div>
  
        {/* Activity Types Filter */}
        <div className="filter-group">
          <label className="filter-label">Activity Types</label>
          <Select
            isMulti
            value={filterData.customActivityName}
            className="basic-single"
            classNamePrefix="select"
            placeholder="Select activity types..."
            isDisabled={false}
            isLoading={false}
            isClearable={true}
            isRtl={false}
            isSearchable={true}
            name="customActivityName"
            options={activityTypes?.map((e: any) => ({
              ...e,
              label: e.label || e.name,
              value: e.value
            }))}
            onChange={(e: any) => handleFilter(e, "customActivityName")}
          />
        </div>
  
        {/* Time Period Filter */}
        <div className="filter-group">
          <label className="filter-label">Time Period</label>
          <Select
            value={filterData.activitySelectedDate}
            className="basic-single"
            classNamePrefix="select"
            placeholder="Select time period..."
            isClearable={true}
            isRtl={false}
            isSearchable={true}
            options={dateLeadAdded}
            onChange={(e: any) =>
              advanceDateFilterOnChange && setActivityFromDate
                ? advanceDateFilterOnChange(
                    e,
                    "activitySelectedDate",
                    setActivityFromDate
                  )
                : null
            }
          />
          
          {datePickerKey === "activitySelectedDate" && (
            <div className="custom-date-container" id="activity-date-container">
              <label className="date-range-label">Custom Date Range</label>
              <div className="date-pickers">
                <div className="date-picker-item">
                  <label>From</label>
                  <DatePicker
                    format={"DD MMM YYYY"}
                    showToday={false}
                    placeholder="Start date"
                    getPopupContainer={() => document.getElementById('activity-date-container') || document.body}
                    onChange={(date: any) => {
                      if (date && setActivityFromDate) {
                        // Convert to UTC at start of day
                        const utcDate = moment.utc(date.format('YYYY-MM-DD')).startOf('day').format();
                        setActivityFromDate(utcDate);
                      }
                    }}
                  />
                </div>
                <div className="date-picker-item">
                  <label>To</label>
                  <DatePicker
                    format={"DD MMM YYYY"}
                    showToday={false}
                    placeholder="End date"
                    getPopupContainer={() => document.getElementById('activity-date-container') || document.body}
                    onChange={(date: any) => {
                      if (date && setActivityToDate) {
                        // Convert to UTC at end of day
                        const utcDate = moment.utc(date.format('YYYY-MM-DD')).endOf('day').format();
                        setActivityToDate(utcDate);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };


  // ADD this component at the end of the file
export const ProductFiltersSection: React.FC<SectionProps> = ({
  filterData,
  handleFilter,
  products = [], // Use props instead of local state
  productsLoading = false, // Use props instead of local state
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  logger.log('🛒 ProductFiltersSection render:', {
    productsCount: products.length,
    loading: productsLoading,
    selectedCount: filterData.products?.length || 0
  });

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const productOptions = filteredProducts.map(product => ({
    value: product._id,
    label: `${product.name} - ₹${product.price.toLocaleString()}`
  }));

  return (
    <>
      <div className="form-group">
        <label htmlFor="products-filter" className="form-label">
          Products
        </label>
      <div 
          id="products-select-container"
          style={{ position: 'relative' }}
        >
          <Select
            value={filterData.products}
            className="basic-single products-select"
            classNamePrefix="select"
            isDisabled={productsLoading}
            isLoading={productsLoading}
            isClearable={true}
            isRtl={false}
            isMulti
            isSearchable={true}
            name="products"
            placeholder={productsLoading ? "Loading products..." : "Search and select products..."}
            options={productOptions}
            menuIsOpen={undefined}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onInputChange={(inputValue) => setSearchTerm(inputValue)}
            onMenuOpen={() => {
              // Simple auto-scroll when dropdown opens
              setTimeout(() => {
                const container = document.getElementById('products-select-container');
                if (container) {
                  const rect = container.getBoundingClientRect();
                  const isNearBottom = rect.bottom > window.innerHeight - 300; // If dropdown would be cut off
                  
                  if (isNearBottom) {
                    // Scroll down to give more space for dropdown
                    window.scrollBy({
                      top: 250,
                      behavior: 'smooth'
                    });
                  }
                }
              }, 100);
            }}
            onChange={(e: any) => {
              logger.log('🛒 Product selection changed:', e?.length || 0);
              handleFilter(e, "products");
            }}
            styles={{
              control: (base: any, state: any) => ({
                ...base,
                minHeight: '48px',
                height: '48px',
                fontSize: '16px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: '500',
                backgroundColor: state.isFocused ? '#ffffff' : '#f8fafc',
                border: `2px solid ${state.isFocused ? '#3b82f6' : '#e2e8f0'}`,
                borderRadius: '8px',
                boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                '&:hover': {
                  borderColor: '#60a5fa',
                  backgroundColor: '#ffffff'
                },
                cursor: 'text',
                transition: 'all 0.2s ease'
              }),
              valueContainer: (base: any) => ({
                ...base,
                padding: '8px 16px',
                fontSize: '16px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                minHeight: '46px',
                flexWrap: 'wrap',
                overflow: 'hidden' // Prevent overflow but allow wrapping
              }),
              input: (base: any) => ({
                ...base,
                margin: 0,
                padding: 0,
                fontSize: '16px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: '400'
              }),
              placeholder: (base: any) => ({
                ...base,
                color: '#9ca3af',
                fontSize: '16px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: '400',
                margin: 0
              }),
              indicatorsContainer: (base: any) => ({
                ...base,
                height: '46px',
                padding: '0 8px'
              }),
              indicatorSeparator: () => ({
                display: 'none'
              }),
              dropdownIndicator: (base: any) => ({
                ...base,
                padding: '8px',
                color: '#6b7280',
                '&:hover': {
                  color: '#374151'
                }
              }),
              clearIndicator: (base: any) => ({
                ...base,
                padding: '8px',
                color: '#6b7280',
                '&:hover': {
                  color: '#ef4444'
                }
              }),
              menu: (base: any) => ({
                ...base,
                fontSize: '16px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                marginTop: '8px',
                marginBottom: '40px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                overflow: 'hidden',
                zIndex: 1000 // Safe z-index, not too high
              }),
              menuList: (base: any) => ({
                ...base,
                padding: '8px',
                maxHeight: '300px',
                paddingBottom: '40px'
              }),
              option: (base: any, state: any) => ({
                ...base,
                fontSize: '16px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: state.isSelected ? '600' : '400',
                padding: '12px 16px',
                backgroundColor: state.isSelected 
                  ? '#3b82f6' 
                  : state.isFocused 
                    ? '#f1f5f9' 
                    : 'white',
                color: state.isSelected ? 'white' : '#1e293b',
                borderRadius: '8px',
                margin: '2px 0',
                '&:hover': {
                  backgroundColor: state.isSelected ? '#2563eb' : '#e2e8f0'
                },
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }),
              multiValue: (base: any) => ({
                ...base,
                backgroundColor: '#dbeafe',
                borderRadius: '6px',
                margin: '2px 4px',
                fontSize: '14px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                maxWidth: '200px' // Prevent individual tags from being too wide
              }),
              multiValueLabel: (base: any) => ({
                ...base,
                color: '#1e40af',
                fontSize: '14px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: '500',
                padding: '4px 8px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }),
              multiValueRemove: (base: any) => ({
                ...base,
                color: '#1e40af',
                padding: '0 6px',
                '&:hover': {
                  backgroundColor: '#bfdbfe',
                  color: '#1d4ed8'
                },
                cursor: 'pointer',
                borderRadius: '0 6px 6px 0'
              }),
              loadingIndicator: (base: any) => ({
                ...base,
                color: '#3b82f6'
              }),
              noOptionsMessage: (base: any) => ({
                ...base,
                fontSize: '16px',
                fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                color: '#6b7280',
                padding: '16px'
              })
            }}
          />
        </div>
        {productsLoading && (
          <div className="text-sm text-gray-500 mt-1">
            Loading products...
          </div>
        )}
        {!productsLoading && products.length === 0 && (
          <div className="text-sm text-red-500 mt-1">
            No products found. Please add products first.
          </div>
        )}
        {filterData.products?.length > 0 && (
          <div className="text-sm text-blue-600 mt-1">
            {filterData.products.length} product(s) selected
          </div>
        )}
      </div>
    </>
  );
};

  export const TaskFiltersSection: React.FC<SectionProps> = ({
    filterData,
    handleFilter,
    advanceDateFilterOnChange,
    taskOptions,
    customLeadOptions,
    datePickerKey,
    setTaskFromDate,
    setTaskToDate,
    getOperatorOptions,
    isOptionsExist,
  }) => {
    return (
      <div className="task-filters-section">
        {/* Description */}
        <div className="section-description">
          Filter leads based on tasks and custom Lead field values
        </div>
        
        {/* Task Type Filter */}
        <div className="filter-group">
          <label className="filter-label">Task Types</label>
          <div className="select-container">
            <Select
              isMulti
              value={filterData.taskType}
              className="basic-single large-select"
              classNamePrefix="select"
              placeholder="Select task types..."
              isDisabled={false}
              isLoading={false}
              isClearable={true}
              isRtl={false}
              isSearchable={true}
              name="taskType"
              options={taskOptions}
              onChange={(e: any) => handleFilter(e, "taskType")}
            />
            <div className="help-tooltip">
              Select multiple task types to filter leads that have these tasks 
            </div>
          </div>
        </div>
  
        {/* Task Due Date Filter */}
        <div className="filter-group">
          <label className="filter-label">Task Due Date Range</label>
          <div className="select-container">
            <Select
              value={filterData.taskSelectedDate}
              className="basic-single large-select"
              classNamePrefix="select"
              placeholder="Select time period..."
              isDisabled={false}
              isLoading={false}
              isClearable={true}
              isRtl={false}
              isSearchable={true}
              name="taskSelectedDate"
              options={dateLeadAdded}
              onChange={(e: any) => {
                console.log('[TaskFiltersSection] taskSelectedDate changed:', e);
                if (advanceDateFilterOnChange && setTaskFromDate) {
                  advanceDateFilterOnChange(
                    e,
                    "taskSelectedDate",
                    setTaskFromDate
                  );
                }
              }}
            />
          </div>
  
          {(datePickerKey === "taskSelectedDate" || filterData.taskSelectedDate?.value === "custom") && (
            <div className="custom-date-container" id="task-date-container">
              <label className="date-range-label">Custom Due Date Range</label>
              <div className="date-pickers">
                <div className="date-picker-item">
                  <label>From</label>
                  <DatePicker
                    format={"DD MMM YYYY"}
                    size="large"
                    showToday={false}
                    placeholder="Start date"
                    getPopupContainer={() => document.getElementById('task-date-container') || document.body}
                    onChange={(date: any) => {
                      if (date && setTaskFromDate) {
                        // Convert to UTC at start of day
                        const utcDate = moment.utc(date.format('YYYY-MM-DD')).startOf('day').format();
                        setTaskFromDate(utcDate);
                      }
                    }}
                  />
                </div>
                <div className="date-picker-item">
                  <label>To</label>
                  <DatePicker
                    format={"DD MMM YYYY"}
                    size="large"
                    showToday={false}
                    placeholder="End date"
                    getPopupContainer={() => document.getElementById('task-date-container') || document.body}
                    onChange={(date: any) => {
                      if (date && setTaskToDate) {
                        // Convert to UTC at end of day
                        const utcDate = moment.utc(date.format('YYYY-MM-DD')).endOf('day').format();
                        setTaskToDate(utcDate);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
  
        {/* Custom Field Filter Section */}
        <div className="custom-field-section">
          <h3 className="custom-field-header">Custom Field Filters</h3>
          <p className="custom-field-description">
            Filter leads based on your Custom Leadform fields
          </p>
  
          {/* Custom Field Selection */}
          <div className="filter-group">
            <label className="filter-label">Select Custom Field</label>
            <div className="select-container">
              <Select
                placeholder="Select custom field..."
                value={filterData.selectCustomLead}
                className="basic-single large-select"
                classNamePrefix="select"
                isClearable={true}
                name="selectCustomLead"
                options={customLeadOptions?.map((e: any) => {
                  const optionCopy = {...e};
                  // Only delete 'options' if it exists (for selection fields)
                  // Keep other properties like 'isGroup' and 'subFields'
                  if (optionCopy.options && !optionCopy.isGroup) {
                    delete optionCopy.options;
                  }
                  console.log('🔍 Custom field option:', optionCopy);
                  return optionCopy;
                })}
                onChange={(e: any) => {
                  console.log('🎯 Custom field selected:', {
                    value: e,
                    isGroup: e?.isGroup,
                    hasSubFields: !!e?.subFields,
                    subFields: e?.subFields
                  });

                  // If it's a group field, clear subfield selection in the same state update
                  if (e?.isGroup) {
                    console.log('📦 Group field selected, setting state with cleared subfield');
                    // Update both at once to avoid state batching issues
                    handleFilter({
                      selectCustomLead: e,
                      selectCustomLeadSubField: null
                    }, "bulk");
                  } else {
                    handleFilter(e, "selectCustomLead");
                  }
                }}
              />
            </div>
          </div>

          {/* Subfield Selection (only for group fields) */}
          {(() => {
            console.log('🔍 Checking subfield dropdown:', {
              isGroup: filterData.selectCustomLead?.isGroup,
              selectedField: filterData.selectCustomLead,
              hasSubFields: !!filterData.selectCustomLead?.subFields,
              subFieldsCount: filterData.selectCustomLead?.subFields?.length
            });
            return filterData.selectCustomLead?.isGroup;
          })() && (
            <div className="filter-group">
              <label className="filter-label">Select Subfield</label>
              <div className="select-container">
                <Select
                  placeholder="Select subfield..."
                  value={filterData.selectCustomLeadSubField}
                  className="basic-single large-select"
                  classNamePrefix="select"
                  isClearable={true}
                  name="selectCustomLeadSubField"
                  options={(() => {
                    const options = filterData.selectCustomLead?.subFields?.map((subField: any) => ({
                      ...subField,
                      label: subField.name,
                      value: `${filterData.selectCustomLead.value}.${subField.value}`,
                      actualValue: subField.value,
                      parentGroup: filterData.selectCustomLead.value
                    }));
                    console.log('📋 Subfield options:', options);
                    return options;
                  })()}
                  onChange={(e: any) => {
                    console.log('🎯 Subfield selected:', e);
                    handleFilter(e, "selectCustomLeadSubField");
                  }}
                />
              </div>
            </div>
          )}

          {/* Only show these sections if a custom field is selected */}
          {((filterData.selectCustomLead && !filterData.selectCustomLead?.isGroup) ||
            (filterData.selectCustomLead?.isGroup && filterData.selectCustomLeadSubField)) && (
            <>
              {/* Operator Selection */}
              <div className="filter-group">
                <label className="filter-label">Condition</label>
                <div className="select-container">
                  <Select
                    placeholder="Select condition..."
                    value={filterData.operator}
                    className="basic-single large-select"
                    classNamePrefix="select"
                    isDisabled={!filterData.selectCustomLead}
                    isLoading={false}
                    isClearable={true}
                    isRtl={false}
                    isSearchable={true}
                    name="operator"
                    options={getOperatorOptions ? getOperatorOptions() : []}
                    onChange={(e: any) => handleFilter(e, "operator")}
                  />
                </div>
              </div>
  
              {/* Field Value */}
              <div className="filter-group">
                <label className="filter-label">Field Value</label>
                
                {isOptionsExist && isOptionsExist()?.length ? (
                  <div>
                    <Select
                      placeholder="Value"
                      value={filterData.leadformValue ? { 
                        label: filterData.leadformValue, 
                        value: filterData.leadformValue 
                      } : null}
                      className="basic-single"
                      classNamePrefix="select"
                      isDisabled={false}
                      isLoading={false}
                      isClearable={true}
                      isRtl={false}
                      isSearchable={true}
                      options={isOptionsExist()}
                      onChange={(e: any) => handleFilter(e?.value || "", "leadformValue")}
                    />
                  </div>
                ) : (
                  <>
                    {/* Check if it's a date field */}
                    {(filterData.selectCustomLeadSubField
                      ? filterData.selectCustomLeadSubField.type
                      : customLeadOptions?.find(
                          (e: any) => e.value === filterData?.selectCustomLead?.value
                        )?.type
                    ) === "date" ? (
  <div className={filterData?.operator?.value === "between" ? "range-inputs" : ""} id="custom-field-date-container">
    <DatePicker
      format={"DD MMM YYYY"}
      placeholder="Select date"
      className="form-control lead_input large-input"
      value={filterData.leadformValue ? moment(filterData.leadformValue) : null}
      disabled={!filterData.operator}
      getPopupContainer={() => document.getElementById('custom-field-date-container') || document.body}
      onChange={(date: any) => {
        if (date) {
          // Convert to UTC at start of day for custom field date
          const utcDate = moment.utc(date.format("YYYY-MM-DD")).startOf('day').format();
          handleFilter(utcDate, "leadformValue");
        } else {
          handleFilter("", "leadformValue");
        }
      }}
    />

    {/* Show second date picker for "between" operator */}
    {filterData?.operator?.value === "between" && (
      <div className="range-separator-container">
        <div className="range-separator">to</div>
        <DatePicker
          format={"DD MMM YYYY"}
          placeholder="Select end date"
          className="form-control lead_input large-input"
          value={filterData.endLeadformValue ? moment(filterData.endLeadformValue) : null}
          getPopupContainer={() => document.getElementById('custom-field-date-container') || document.body}
          onChange={(date: any) => {
            if (date) {
              // Convert to UTC at end of day for custom field end date
              const utcDate = moment.utc(date.format("YYYY-MM-DD")).endOf('day').format();
              handleFilter(utcDate, "endLeadformValue");
            } else {
              handleFilter("", "endLeadformValue");
            }
          }}
        />
      </div>
    )}
  </div>
) : (
                      /* For number and amount fields */
                      ((filterData.selectCustomLeadSubField
                        ? filterData.selectCustomLeadSubField.type
                        : customLeadOptions?.find(
                            (e: any) => e.value === filterData?.selectCustomLead?.value
                          )?.type
                      ) === "number" ||
                      (filterData.selectCustomLeadSubField
                        ? filterData.selectCustomLeadSubField.type
                        : customLeadOptions?.find(
                            (e: any) => e.value === filterData?.selectCustomLead?.value
                          )?.type
                      ) === "amount") ? (
                        <div className={filterData?.operator?.value === "between" ? "range-inputs" : ""}>
                          <input
                            type="number"
                            placeholder={
                              filterData?.operator?.value === "between"
                                ? "Enter Start Value"
                                : "Enter value"
                            }
                            className="form-control lead_input large-input"
                            value={filterData.leadformValue}
                            disabled={!filterData.operator}
                            onChange={(e: any) =>
                              handleFilter(e.target.value, "leadformValue")
                            }
                          />
                          
                          {/* Show second input for "between" operator */}
                          {filterData?.operator?.value === "between" && (
                            <div className="range-separator-container">
                              <div className="range-separator">to</div>
                              <input
                                type="number"
                                placeholder="Enter End Value"
                                className="form-control lead_input large-input"
                                value={filterData.endLeadformValue}
                                onChange={(e: any) =>
                                  handleFilter(e.target.value, "endLeadformValue")
                                }
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        /* For all other field types */
                        <div className={filterData?.operator?.value === "between" ? "range-inputs" : ""}>
                          <input
                            type="text"
                            placeholder={
                              filterData?.operator?.value === "between"
                                ? "Enter Start Value"
                                : "Enter value"
                            }
                            className="form-control lead_input large-input"
                            value={filterData.leadformValue}
                            disabled={!filterData.operator}
                            onChange={(e: any) =>
                              handleFilter(e.target.value, "leadformValue")
                            }
                          />
                          
                          {/* Show second input for "between" operator */}
                          {filterData?.operator?.value === "between" && (
                            <div className="range-separator-container">
                              <div className="range-separator">to</div>
                              <input
                                type="text"
                                placeholder="Enter End Value"
                                className="form-control lead_input large-input"
                                value={filterData.endLeadformValue}
                                onChange={(e: any) =>
                                  handleFilter(e.target.value, "endLeadformValue")
                                }
                              />
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
              
              {/* Active Custom Field Filters */}
              {filterData.selectCustomLead && filterData.operator && filterData.leadformValue && (
                <div className="active-filters">
                  <div className="active-filters-header">Active Custom Field Filter:</div>
                  <div className="filter-tag">
                    <span className="filter-tag-field">{filterData.selectCustomLead.label}</span>
                    <span className="filter-tag-operator">{filterData.operator.label}</span>
                    <span className="filter-tag-value">
                      {filterData.operator.value === "between" 
                        ? `${filterData.leadformValue} to ${filterData.endLeadformValue}` 
                        : filterData.leadformValue}
                    </span>
                  </div>
                </div>
              )}

              
            </>

            
          )}

          
        </div>
      </div>
    );
  };

  